// apps/website/src/components/admin/tasks/TaskInspectorPanel.tsx

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, AlertTriangle } from 'lucide-react';

import { useUiStore } from '../../../../../../apps/website/src/stores/ui';
import type { UiStoreState } from '../../../../../../apps/website/src/stores/ui';
import { useAutoSaveTask } from '@dopomogai/agent-kit';
import { Button, IconButton, Spinner, Alert } from '@dopomogai/ui';
import type { ProjectTask } from '@dopomogai/types';

import { TaskEditorForm } from './TaskEditorForm';

interface TaskInspectorPanelProps {
  isCreating: boolean;
  onCreate: (task: ProjectTask, context: any) => void;
}

/**
 * Displays the auto-save status with feedback.
 */
const SaveStatusIndicator: React.FC = () => {
  const { saveStatus, setSaveStatus } = useUiStore((state: UiStoreState) => ({
    saveStatus: state.taskInspector.saveStatus,
    setSaveStatus: state.setSaveStatus,
  }));

  useEffect(() => {
    if (saveStatus === 'success') {
      const timer = setTimeout(() => setSaveStatus('idle'), 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [saveStatus, setSaveStatus]);

  switch (saveStatus) {
    case 'saving':
      return (
        <div className="flex items-center gap-2 text-sm text-slate-500 font-medium">
          <Spinner size="sm" /> Saving...
        </div>
      );
    case 'success':
      return (
        <div className="flex items-center gap-2 text-sm text-emerald-600 font-medium">
          <CheckCircle size={16} /> Saved
        </div>
      );
    case 'error':
      return (
        <div className="flex items-center gap-2 text-sm text-red-600 font-medium">
          <AlertTriangle size={16} /> Save failed
        </div>
      );
    default:
      return <div className="h-[20px]" />;
  }
};

/**
 * Side panel for creating, viewing, and editing tasks.
 */
export const TaskInspectorPanel: React.FC<TaskInspectorPanelProps> = ({ isCreating, onCreate }) => {
  const { t } = useTranslation(['admin_tasks', 'common']);

  const { taskInspector, closeTaskInspector, updateEditingTask } = useUiStore();
  const { isOpen, editingTask, isNew, context, changes } = taskInspector;

  const { error: autoSaveError } = useAutoSaveTask();

  const handleCreate = () => {
    if (editingTask && context) {
      if (!editingTask._id.trim()) {
        alert(t('editor.idRequiredError'));
        return;
      }
      if (!editingTask.title.trim()) {
        alert(t('editor.titleRequiredError'));
        return;
      }
      onCreate(editingTask, context);
    }
  };

  const handleClose = () => {
    if (!isNew && Object.keys(changes).length > 0) {
      if (window.confirm(t('editor.unsavedChangesWarning'))) {
        closeTaskInspector();
      }
    } else {
      closeTaskInspector();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-40 flex justify-end">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="absolute inset-0 bg-black/40"
            onClick={handleClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 350, damping: 40 }}
            className="relative w-full max-w-2xl h-full bg-slate-50 shadow-2xl flex flex-col"
            aria-modal="true"
            role="dialog"
            aria-labelledby="task-inspector-title"
          >
            {editingTask ? (
              <>
                {/* Header */}
                <header className="flex-shrink-0 p-4 bg-white border-b-2 border-black flex justify-between items-center h-20">
                  <div>
                    <h2 id="task-inspector-title" className="text-xl font-bold text-slate-800">
                      {isNew ? t('editor.newTaskTitle') : editingTask._id}
                    </h2>
                    {!isNew && (
                      <div className="mt-1">
                        <SaveStatusIndicator />
                      </div>
                    )}
                  </div>
                  <IconButton icon={<X />} onClick={handleClose} ariaLabel={t('common:close')} />
                </header>

                {/* Body */}
                <main className="flex-grow p-6 md:p-8 overflow-y-auto">
                  {autoSaveError && (
                    <Alert
                      type="error"
                      title="Auto-save Failed"
                      message={autoSaveError.message || 'An unknown error occurred.'}
                      className="mb-6"
                    />
                  )}

                  <TaskEditorForm taskData={editingTask} onDataChange={updateEditingTask} />
                </main>

                {/* Footer (new tasks only) */}
                {isNew && (
                  <footer className="flex-shrink-0 p-4 bg-white border-t-2 border-black flex justify-end gap-3">
                    <Button textKey="common:cancel" variant="secondary" onClick={handleClose} disabled={isCreating} />
                    <Button textKey="common:createTask" onClick={handleCreate} loading={isCreating} />
                  </footer>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <Spinner text="Loading Task..." />
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};